import { useState } from "react";
import { useQuery, keepPreviousData, type QueryKey } from "@tanstack/react-query";
import { fetchPaged, pageSizeOptions } from "@/api/paged";
import type { SortSpec, SpringPage } from "@/api/types";

export interface UsePagedQueryOptions<F> {
  key: QueryKey;
  path: string;
  initialFiltro: F;
  initialSort?: SortSpec;
  initialSize?: number;
  /**
   * Parte do filtro controlada fora do formulário (ex. o período da página):
   * vai no corpo e na query key direto, sem esperar o "Buscar", e volta pra página 0.
   */
  extraFiltro?: Partial<F>;
}

export interface PagedQueryResult<T, F> {
  rows: T[];
  page: SpringPage<T> | undefined;
  pageIndex: number;
  pageSize: number;
  sort?: SortSpec;
  /** Rascunho editável, ligado ao formulário de filtro — não dispara request. */
  draft: F;
  setDraft: (updater: F | ((f: F) => F)) => void;
  /** Filtro efetivamente enviado ao servidor. */
  filtro: F;
  /** Comita `draft` em `filtro` e volta pra página 0 — o botão "Buscar". */
  search: () => void;
  /** Troca draft e filtro de uma vez e busca — atalhos que não passam pelo formulário. */
  applyFiltro: (f: F) => void;
  setPageIndex: (i: number) => void;
  setPageSize: (n: number) => void;
  setSort: (s: SortSpec | undefined) => void;
  pageSizeOptions: number[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Porte de PaginationService (app/services/pagination.service.js) pro mundo
 * react-query. Ponto de design deliberado: `draft` e `filtro` são estados
 * separados — o legado só refaz a busca no clique em "Buscar"; se o draft
 * entrasse direto na query key, viraria um request por tecla digitada.
 */
export function usePagedQuery<T, F>({
  key,
  path,
  initialFiltro,
  initialSort,
  initialSize = 10,
  extraFiltro,
}: UsePagedQueryOptions<F>): PagedQueryResult<T, F> {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(initialSize);
  const [sort, setSort] = useState<SortSpec | undefined>(initialSort);
  const [draft, setDraft] = useState<F>(initialFiltro);
  const [filtro, setFiltro] = useState<F>(initialFiltro);

  // Ajuste de estado durante o render (padrão do React para "resetar ao mudar uma prop").
  const extraKey = JSON.stringify(extraFiltro ?? null);
  const [extraAnterior, setExtraAnterior] = useState(extraKey);
  if (extraKey !== extraAnterior) {
    setExtraAnterior(extraKey);
    setPageIndex(0);
  }

  const query = useQuery({
    queryKey: [...key, { pageIndex, pageSize, sort, filtro, extraFiltro }],
    queryFn: ({ signal }) =>
      fetchPaged<T, F>(path, { page: pageIndex, size: pageSize, sort, filtro: { ...filtro, ...extraFiltro } }, signal),
    placeholderData: keepPreviousData,
  });

  const search = () => {
    setFiltro(draft);
    setPageIndex(0);
  };

  const applyFiltro = (f: F) => {
    setDraft(f);
    setFiltro(f);
    setPageIndex(0);
  };

  return {
    rows: query.data?.content ?? [],
    page: query.data,
    pageIndex,
    pageSize,
    sort,
    draft,
    setDraft: (updater) => setDraft((prev) => (typeof updater === "function" ? (updater as (f: F) => F)(prev) : updater)),
    filtro,
    search,
    applyFiltro,
    setPageIndex,
    setPageSize: (n) => {
      setPageSize(n);
      setPageIndex(0);
    },
    setSort,
    pageSizeOptions: pageSizeOptions(query.data?.totalElements ?? 0, pageSize),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
